// src/components/GlobalAuthErrorModal.js
//
// Escucha el evento 'educat:auth-blocked' que dispara fetchApi() en
// api.js cuando un 401 sobrevive al intento de refresh de token (sesión
// no autorizada para esta app, expirada, o inválida). Se monta UNA sola
// vez en la raíz de la app (App.js) -- así cualquier llamada a la API,
// desde cualquier página, dispara el mismo bloqueo sin que cada página
// tenga que manejar el 401 por su cuenta.
//
// A propósito no se puede cerrar con click afuera ni con Escape --
// mientras la sesión esté rota, no tiene sentido dejar seguir usando
// la app (los demás datos en pantalla ya están potencialmente
// desactualizados o van a fallar igual).

import React, { useEffect, useState } from 'react';
import { Dialog, DialogTitle, DialogContent, DialogActions, Typography, Button, Box } from '@mui/material';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import { useAuth } from '../context/AppContext';
import { useTheme } from '../context/ThemeContext';
import { MODO_OPERACION } from '../services/api';

export default function GlobalAuthErrorModal() {
  const { logout } = useAuth();
  const { productoInfo } = useTheme();
  const [bloqueo, setBloqueo] = useState(null); // null = sin bloqueo activo

  // Cuenta sin permiso para esta app: el texto usa el nombre visible del
  // producto (Aulas Virtuales en Fenagro, EducaT en Redeprog) y no el que
  // venga fijo desde el backend.
  const esFenagro = String(MODO_OPERACION).toLowerCase() !== 'redeprog';
  const mensaje = bloqueo?.codigo === 'SecurityTokenInvalidAudienceException'
    ? `Tu cuenta no está autorizada para usar ${productoInfo?.nombre || 'esta aplicación'} todavía. ${esFenagro
        ? 'Pide a Fenagro que te autorice esta aplicación.'
        : 'Pide al administrador de tu institución que te autorice.'}`
    : (bloqueo?.mensaje || 'Tu sesión ya no es válida.');

  useEffect(() => {
    const onBloqueo = (e) => setBloqueo(e.detail);
    window.addEventListener('educat:auth-blocked', onBloqueo);
    return () => window.removeEventListener('educat:auth-blocked', onBloqueo);
  }, []);

  const handleCerrarSesion = () => {
    setBloqueo(null);
    logout();
    window.location.href = '/login';
  };

  return (
    <Dialog
      open={!!bloqueo}
      disableEscapeKeyDown
      onClose={(e, reason) => { if (reason === 'backdropClick' || reason === 'escapeKeyDown') return; }}
      maxWidth="xs"
      fullWidth
    >
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
        <LockOutlinedIcon color="error" />
        Sesión bloqueada
      </DialogTitle>
      <DialogContent>
        <Typography color="text.secondary">
          {mensaje}
        </Typography>
      </DialogContent>
      <DialogActions sx={{ p: 2, pt: 0 }}>
        <Box sx={{ width: '100%' }}>
          <Button fullWidth variant="contained" onClick={handleCerrarSesion}>
            Cerrar sesión
          </Button>
        </Box>
      </DialogActions>
    </Dialog>
  );
}
